import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate, Navigate, Link as RouterLink } from 'react-router-dom';
import { verifyPasswordReset, confirmPasswordReset } from '../services/authService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

/**
 * ResetPassword
 *
 * Landing page for password-reset email links.
 * URL patterns:
 *   - https://questguru.in/reset-password?mode=resetPassword&oobCode=XXXX
 *   - https://questguru.in/__/auth/action?mode=resetPassword&oobCode=XXXX
 *
 * Flow:
 *  1. On mount: verifyPasswordReset(oobCode) - confirms link is valid and fetches email
 *  2. User enters and confirms new password
 *  3. confirmPasswordReset(oobCode, newPassword) - commits the change via backend
 *  4. Success: shows deep-link button to open the QuestGuru app + web login fallback
 */

const APP_DEEP_LINK = 'QuestGuru://login';

const extractOobCodeFromUrl = (rawUrl) => {
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl);
    const direct = parsed.searchParams.get('oobCode') || parsed.searchParams.get('code') || '';
    if (direct) return direct;

    const nestedKeys = ['link', 'continueUrl', 'deep_link_id'];
    for (const key of nestedKeys) {
      const nested = parsed.searchParams.get(key);
      if (!nested) continue;
      const nestedCode = extractOobCodeFromUrl(decodeURIComponent(nested));
      if (nestedCode) return nestedCode;
    }
  } catch (_) {
    // Best effort parse only.
  }
  return '';
};

const getOobCodeFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const direct = params.get('oobCode') || params.get('code') || '';
  if (direct) return direct;

  const wrappedKeys = ['link', 'continueUrl', 'deep_link_id'];
  for (const key of wrappedKeys) {
    const wrapped = params.get(key);
    if (!wrapped) continue;
    const code = extractOobCodeFromUrl(decodeURIComponent(wrapped));
    if (code) return code;
  }

  return '';
};

export default function ResetPassword() {
  const location = useLocation();

  // All hooks must come before any conditional return (Rules of Hooks).
  const [status, setStatus] = useState('validating'); // validating | ready | loading | success | expired | error
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    const oobCode = getOobCodeFromSearch(location.search);

    if (!oobCode) {
      setStatus('expired');
      setErrorMessage('No reset code found in this link. Please request a new password reset.');
      return;
    }

    // Show the form immediately so users are not blocked behind the verify call.
    setStatus('ready');
    setIsVerifying(true);
    setErrorMessage('');
    setVerifiedEmail('');

    (async () => {
      try {
        const result = await verifyPasswordReset(oobCode);
        setVerifiedEmail(result?.email || '');
      } catch (err) {
        const message = String(err?.message || '').toLowerCase();
        if (message.includes('expired') || message.includes('invalid') || message.includes('used')) {
          setStatus('expired');
          setErrorMessage('This password reset link has expired or was already used. Please request a new one.');
        } else {
          // Keep the form visible on transient verification failures so users can retry.
          setErrorMessage(err?.message || 'Failed to validate the reset link.');
        }
      } finally {
        setIsVerifying(false);
      }
    })();
  }, [location.search]);

  // Defensive guard: Firebase Console may be (or has historically been) misconfigured
  // with /reset-password as the action handler URL for ALL Firebase email action types.
  // Redirect verifyEmail / recoverEmail actions to the proper FirebaseActionRoute.
  const _actionMode = new URLSearchParams(location.search).get('mode');
  if (_actionMode === 'verifyEmail' || _actionMode === 'recoverEmail') {
    return <Navigate to={`/auth/action${location.search}`} replace />;
  }

  const getOobCode = () => getOobCodeFromSearch(location.search);

  const validate = () => {
    if (!newPassword) return 'Please enter a new password.';
    if (newPassword.length < 6) return 'Password must be at least 6 characters.';
    if (newPassword !== confirmPassword) return 'Passwords do not match.';
    return '';
  };

  const handleReset = async () => {
    const err = validate();
    if (err) {
      setFieldError(err);
      return;
    }

    setFieldError('');
    setStatus('loading');

    try {
      await confirmPasswordReset(getOobCode(), newPassword);
      setStatus('success');
    } catch (err) {
      const message = String(err?.message || '').toLowerCase();
      setStatus('ready');
      if (message.includes('expired') || message.includes('invalid')) {
        setFieldError('This link has expired. Please request a new password reset.');
      } else if (message.includes('weak-password') || message.includes('too short')) {
        setFieldError('Password is too weak. Please choose a stronger password (min. 6 characters).');
      } else {
        setFieldError(err?.message || 'Failed to reset password. Please try again.');
      }
    }
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Card elevation={1} sx={{ borderRadius: 3, maxWidth: 480, mx: 'auto' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack spacing={2.5} alignItems="center">
              {status === 'validating' && (
                <Stack spacing={2} alignItems="center">
                  <CircularProgress />
                  <Typography color="text.secondary">Verifying your reset link…</Typography>
                </Stack>
              )}

              {status === 'expired' && (
                <Stack spacing={2} alignItems="center">
                  <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
                  <Typography variant="h6" fontWeight={700}>Link expired or invalid</Typography>
                  <Typography color="text.secondary" align="center">{errorMessage}</Typography>
                  <Button variant="contained" component={RouterLink} to="/login" sx={{ mt: 1 }}>
                    Back to Login
                  </Button>
                </Stack>
              )}

              {status === 'error' && (
                <Stack spacing={2} alignItems="center">
                  <ErrorOutlineIcon sx={{ fontSize: 56, color: 'warning.main' }} />
                  <Typography variant="h6" fontWeight={700}>Something went wrong</Typography>
                  <Typography color="text.secondary" align="center">{errorMessage}</Typography>
                  <Button variant="outlined" component={RouterLink} to="/login">Back to Login</Button>
                </Stack>
              )}

              {(status === 'ready' || status === 'loading') && (
                <>
                  <LockOutlinedIcon sx={{ fontSize: 48, color: 'primary.main', mb: -1 }} />
                  <Typography variant="h5" fontWeight={800}>Set new password</Typography>
                  {isVerifying && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Verifying your reset link…</Typography>
                    </Stack>
                  )}
                  {verifiedEmail && (
                    <Typography color="text.secondary" align="center" variant="body2">
                      Resetting password for <strong>{verifiedEmail}</strong>
                    </Typography>
                  )}

                  <Stack spacing={2} sx={{ width: '100%', mt: 1 }}>
                    <TextField
                      label="New password"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setFieldError('');
                      }}
                      fullWidth
                      size="medium"
                      disabled={status === 'loading'}
                      error={!!fieldError && (!newPassword || newPassword.length < 6)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowNew((v) => !v)} edge="end" size="small">
                              {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Confirm new password"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setFieldError('');
                      }}
                      fullWidth
                      size="medium"
                      disabled={status === 'loading'}
                      error={!!fieldError && newPassword !== confirmPassword}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleReset();
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small">
                              {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {fieldError && (
                      <Typography color="error" variant="body2" sx={{ mt: -1 }}>
                        {fieldError}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                      Minimum 6 characters
                    </Typography>

                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleReset}
                      disabled={status === 'loading'}
                      fullWidth
                      sx={{ mt: 1, py: 1.4, fontWeight: 700, fontSize: '1rem' }}
                    >
                      {status === 'loading' ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
                    </Button>
                  </Stack>
                </>
              )}

              {status === 'success' && (
                <Stack spacing={2.5} alignItems="center">
                  <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
                  <Typography variant="h5" fontWeight={800}>Password updated!</Typography>
                  <Typography color="text.secondary" align="center">
                    Your password has been reset successfully. You can now sign in with your new password.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ py: 1.4, fontWeight: 700, fontSize: '1rem' }}
                    onClick={() => {
                      window.location.href = APP_DEEP_LINK;
                    }}
                  >
                    Open QuestGuru App
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    sx={{ fontWeight: 600 }}
                  >
                    Sign In on Web
                  </Button>

                  <Typography variant="caption" color="text.secondary" align="center">
                    If the app does not open automatically, tap "Open QuestGuru App" above or use the web login.
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
